import { type HandoverBlocker, REVIEW_HANDOVERS, type Language, type ReviewHandover } from '@eformulieren/engine'
import { type ReactNode, useId, useRef, useState } from 'react'
import { BusyButton } from './BusyButton.js'
import { ConfirmQuestion } from './ConfirmQuestion.js'

/**
 * De drie handelingen waarmee de toetser zijn beurt sluit (R-181, R-173, ontwerp 7.8 voorstel 1).
 *
 * **Waar ze staan.** In de actiebalk onderaan, op de plek van Indienen in de andere gedaanten: het
 * look-and-feel-canvas toont ze daar als de knoppen van de toetsweergave, en het aangeboden scherm
 * W027 in dezelfde volgorde. Terugleggen is secundair, afkeuren draagt de kleur van een
 * onomkeerbare handeling, goedkeuren is de primaire.
 *
 * **Wanneer ze aan gaan komt niet van hier.** De aanroeper geeft per handeling het beletsel mee
 * dat `beletselVoorOverdracht` in de engine geeft, dezelfde functie waarmee de pod weigert, en
 * of de harde controles van de eigen laag openstaan, dezelfde telling als bij Indienen. Deze
 * component tekent alleen de uitkomst, plus een zin die zegt waarom een knop nog uit staat; via
 * `aria-describedby` hoort een schermlezer die zin bij de knop (R-139, R-140). Terugleggen staat
 * ook uit zolang een sectie die aanpassing vraagt geen opmerking voor de aanvrager heeft (R-75,
 * R-132); de zin noemt die secties.
 *
 * **Goedkeuren en afkeuren vragen een bevestiging, terugleggen niet.** Dat is 7.8 voorstel 1
 * letterlijk: "goedkeuren en afkeuren vragen een expliciete bevestiging". Na die twee is de lijn
 * af en komt er geen beurt meer (7.3); na terugleggen krijgt de invuller een aanvulbeurt en kan
 * de toetser bij de hertoets opnieuw oordelen (R-181, cyclisch herzien).
 */

const NAME: Record<ReviewHandover, Record<Language, string>> = {
  // "Naar de aanvrager" en niet "ter aanvulling" (ontwerp van het toetsscherm, 29 oktober 2026):
  // de knop zegt waar de beurt heen gaat. De uitkomst is dezelfde: gelegenheid tot correctie (R-181).
  teruggelegd: { nl: 'Terugleggen naar de aanvrager', en: 'Return to the applicant' },
  afgekeurd: { nl: 'Afkeuren', en: 'Reject' },
  goedgekeurd: { nl: 'Goedkeuren', en: 'Approve' },
}

/** Wat de knop zegt terwijl hij op de pod wacht (`BusyButton`). */
const BUSY: Record<ReviewHandover, Record<Language, string>> = {
  teruggelegd: { nl: 'Bezig met terugleggen…', en: 'Returning…' },
  afgekeurd: { nl: 'Bezig met afkeuren…', en: 'Rejecting…' },
  goedgekeurd: { nl: 'Bezig met goedkeuren…', en: 'Approving…' },
}

const KIND: Record<ReviewHandover, 'secundair' | 'gevaar' | 'primair'> = {
  teruggelegd: 'secundair',
  afgekeurd: 'gevaar',
  goedgekeurd: 'primair',
}

/** De vraag vóór een eindoordeel, met de uitkomst van R-181 erin zoals 7.3 hem noemt. */
const REQUEST: Record<'goedgekeurd' | 'afgekeurd', { kop: Record<Language, string>; gevolg: Record<Language, string> }> = {
  goedgekeurd: {
    kop: { nl: 'Goedkeuren?', en: 'Approve?' },
    gevolg: {
      nl: 'De toets sluit met de uitkomst: in behandeling. Dit is niet terug te draaien.',
      en: 'The check closes with the outcome: accepted for processing. This cannot be undone.',
    },
  },
  afgekeurd: {
    kop: { nl: 'Afkeuren?', en: 'Reject?' },
    gevolg: {
      nl: 'De toets sluit met de uitkomst: definitief niet in behandeling. Dit is niet terug te draaien.',
      en: 'The check closes with the outcome: definitively not accepted for processing. This cannot be undone.',
    },
  },
}

/**
 * Waarom een knop uit staat, in een zin die de toetser begrijpt.
 *
 * Geëxporteerd voor de voet: die zet deze reden in dezelfde zin als de voortgang, zodat er één
 * zin en één reden staat (ontwerp van het toetsscherm) en de knoppen via `aria-describedby` naar
 * die zin wijzen.
 */
export function reviewReason(
  blockers: Readonly<Record<ReviewHandover, HandoverBlocker | undefined>>,
  hardChecksOpen: boolean,
  language: Language,
  sectionName: (sectionId: string) => string,
): string | undefined {
  if (hardChecksOpen) {
    return language === 'nl'
      ? 'Eerst moet alles in de toets zijn ingevuld.'
      : 'First every hard check of the review has to pass.'
  }
  const missing = REVIEW_HANDOVERS.map((name) => blockers[name]).find(
    (blocker) => blocker?.code === 'oordeel-ontbreekt',
  )
  if (missing?.code === 'oordeel-ontbreekt') {
    // Hoeveel er nog openstaan zegt de voortgang ervoor al; hier alleen wat dat betekent.
    return language === 'nl'
      ? 'Terugleggen, afkeuren of goedkeuren kan zodra elk criterium een oordeel heeft.'
      : 'Returning, rejecting or approving is possible once every criterion has a judgement.'
  }
  if (blockers.goedgekeurd?.code === 'criterium-voldoet-niet' && blockers.teruggelegd?.code === 'opmerking-ontbreekt') {
    // Beide zijn waar; de opmerking is wat de toetser nu kan doen, en goedkeuren volgt vanzelf
    // uit de criteria.
    const names = blockers.teruggelegd.secties.map(sectionName).join(', ')
    return language === 'nl'
      ? `Goedkeuren kan als alle criteria voldoen. Terugleggen vraagt eerst een opmerking voor de aanvrager bij: ${names}.`
      : `Approving is possible once every criterion is met. Returning first needs a comment for the applicant on: ${names}.`
  }
  if (blockers.goedgekeurd?.code === 'criterium-voldoet-niet') {
    // Besluit van 30 oktober 2026: in behandeling nemen kan niet zolang een criterium niet voldoet.
    return language === 'nl' ? 'Goedkeuren kan als alle criteria voldoen.' : 'Approving is possible once every criterion is met.'
  }
  if (blockers.teruggelegd?.code === 'niets-om-te-corrigeren') {
    return language === 'nl'
      ? 'Terugleggen kan alleen als een criterium niet voldoet.'
      : 'Returning for correction is only possible when a criterion is not met.'
  }
  const comment = blockers.teruggelegd
  if (comment?.code === 'opmerking-ontbreekt') {
    // De secties bij naam: de toetser moet weten in welke toetssectie hij nog moet schrijven
    // (R-75, R-132). Het opmerkingveld staat in de toetssectie die over deze sectie gaat.
    const names = comment.secties.map(sectionName).join(', ')
    return language === 'nl'
      ? `Terugleggen vraagt eerst een opmerking voor de aanvrager bij: ${names}.`
      : `Returning for correction first needs a comment for the applicant on: ${names}.`
  }
  return undefined
}

export interface ReviewButtonsProps {
  readonly taal: Language
  /** Per handeling wat haar nu tegenhoudt, uit `beletselVoorOverdracht` in de engine. */
  readonly blockers: Readonly<Record<ReviewHandover, HandoverBlocker | undefined>>
  /** Of er in de eigen laag nog een harde controle openstaat (R-81, dezelfde telling als Indienen). */
  readonly hardChecksOpen: boolean
  /** Geeft hij een belofte terug, dan staat de knop bezig tot de pod heeft geantwoord. */
  readonly onHandover: (event: ReviewHandover) => void | Promise<void>
  /** De titel van een sectie in de taal van de lezer; zonder deze functie het id. */
  readonly sectionName?: (sectionId: string) => string
  /**
   * Het id van de zin elders (in de voet) die de reden al draagt. Dan tekent deze component de
   * reden niet zelf, maar wijzen de knoppen die uit staan naar die zin.
   */
  readonly reasonId?: string
  /**
   * Een reden waarom geen enkele uitkomst nu kan, los van de criteria, zoals een verlopen sessie.
   * Dan staan alle drie uit en is dit de reden.
   */
  readonly blockedBy?: string
}

export function ReviewButtons({
  taal: language,
  blockers,
  hardChecksOpen,
  onHandover,
  sectionName = (sectionId) => sectionId,
  reasonId: externalReasonId,
  blockedBy,
}: ReviewButtonsProps): ReactNode {
  const [requests, setRequests] = useState<'goedgekeurd' | 'afgekeurd' | undefined>(undefined)
  const buttons = useRef(new Map<ReviewHandover, HTMLButtonElement | null>())
  const ownReasonId = useId()
  const reasonId = externalReasonId ?? ownReasonId
  const why = blockedBy ?? reviewReason(blockers, hardChecksOpen, language, sectionName)

  /** Terug naar de knop die de vraag opende: wie annuleert, staat weer waar hij was. */
  function closeDialog(): void {
    const callback = requests === undefined ? undefined : buttons.current.get(requests)
    setRequests(undefined)
    callback?.focus()
  }

  return (
    <div className="ef-toetsknoppen">
      {why === undefined || externalReasonId !== undefined ? null : (
        <p id={reasonId} className="ef-toetsknoppen__reden">
          {why}
        </p>
      )}
      <div className="ef-toetsknoppen__rij">
        {REVIEW_HANDOVERS.map((event) => {
          const out = blockedBy !== undefined || hardChecksOpen || blockers[event] !== undefined
          return (
            <BusyButton
              key={event}
              buttonRef={(element) => {
                buttons.current.set(event, element)
              }}
              className="ef-actieknop"
              data-soort={KIND[event]}
              disabled={out}
              busyText={BUSY[event][language]}
              {...(out && why !== undefined ? { 'aria-describedby': reasonId } : {})}
              onAction={() => {
                // Terugleggen meteen; de twee eindoordelen vragen eerst een bevestiging, en daar
                // wacht de knop in de vraag op de pod.
                if (event === 'teruggelegd') return onHandover(event)
                setRequests(event)
                return undefined
              }}
            >
              {NAME[event][language]}
            </BusyButton>
          )
        })}
      </div>
      {requests === undefined ? null : (
        <ConfirmQuestion
          kop={REQUEST[requests].kop[language]}
          consequence={REQUEST[requests].gevolg[language]}
          proceed={NAME[requests][language]}
          soort={KIND[requests]}
          cancel={language === 'nl' ? 'Annuleren' : 'Cancel'}
          busyText={BUSY[requests][language]}
          onProceed={async () => {
            // De vraag blijft staan terwijl de knop erin wacht; na het antwoord van de pod toont de
            // pagina de nieuwe stand, of een melding waarom het niet doorging.
            const chosen = requests
            await onHandover(chosen)
            setRequests(undefined)
          }}
          onCancel={closeDialog}
        />
      )}
    </div>
  )
}
